import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import LessonFlowSection from './components/LessonFlowSection';
import { Link } from 'react-router-dom';

function useVisible(threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const forItems = [
  '完全沒有音樂基礎的成人初學者',
  '想為生活保留一段安靜時光的人',
  '希望孩子學音樂但不想有過度競爭壓力的家長',
  '曾在學習中感到挫折、想重新開始的人',
  '喜歡東方音樂美感、對古箏聲音有共鳴的人',
  '對學習有自己節奏、不希望被催趕的人',
];

const notForItems = [
  {
    text: '以考級、比賽、短期成果為主要目標',
    detail: '這裡重視過程感受，而非檢定成果',
  },
  {
    text: '需要高強度密集訓練的學習者',
    detail: '每週一次或兩週一次的頻率，可能不夠',
  },
  {
    text: '希望快速達到演出或表演水準',
    detail: '步調較慢，重視扎根而非速成',
  },
  {
    text: '偏好固定進度表與結構化課綱',
    detail: '課程依你的狀態動態調整',
  },
  {
    text: '需要頻繁的成果驗收與考核',
    detail: '不打分、不排名',
  },
  {
    text: '期待競爭氛圍激勵學習',
    detail: '教室氛圍安靜溫柔，彼此支持',
  },
];

const methods = [
  {
    icon: 'ri-user-heart-line',
    title: '一對一個人課',
    tagline: '每堂課都是為你量身訂製的學習體驗',
    audience: '需要彈性時間、希望深度個人化指導的學習者',
    atmosphere: '安靜、專注、完全跟隨你的步調。不需要配合他人，不需要假裝跟上。',
    rhythm: '每堂課根據你當下的狀態動態調整內容，沒有固定的進度壓力。',
    scenario: '時間不固定、有特定學習目標、或偏好一對一深度對話的你。',
    tags: ['完全客製', '彈性時間', '深度陪伴'],
  },
  {
    icon: 'ri-group-line',
    title: '小班制共學課',
    tagline: '保有學習的共鳴感，也維持足夠的個人關注空間',
    audience: '喜歡有同伴一起學習、享受互相觀摩成長的人',
    atmosphere: '溫暖、輕鬆、彼此支持而非比較。2–4 人的小班規模，每個人都有表達空間。',
    rhythm: '以團體共識為基礎安排進度，同時保有個人調整的彈性空間。',
    scenario: '想體驗共學樂趣、喜歡從他人的學習中獲得靈感與動力的你。',
    tags: ['2–4人小班', '互相支持', '共學樂趣'],
  },
];

const rhythmItems = [
  {
    icon: 'ri-calendar-line',
    color: '#7a6248',
    q: '上課頻率',
    a: '建議每週一次，也可依生活狀態調整為兩週一次。',
  },
  {
    icon: 'ri-pause-circle-line',
    color: '#7a8b6e',
    q: '暫停與調整',
    a: '生活有起伏是正常的。需要暫停或降低頻率，提前告知即可，不需要解釋太多。',
  },
  {
    icon: 'ri-compass-3-line',
    color: '#9a8470',
    q: '進度安排',
    a: '沒有固定進度表。你的感受與狀態，是每堂課最重要的參考依據。',
  },
  {
    icon: 'ri-heart-pulse-line',
    color: '#4a6741',
    q: '學習評估',
    a: '不打分數、不排名，定期回顧是為了讓你看見自己的成長，而不是設定新壓力。',
  },
];

export default function Teaching() {
  const hero = useVisible(0.05);
  const audience = useVisible();
  const methodSec = useVisible();
  const rhythm = useVisible();
  const cta = useVisible();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-28 bg-[#f2ede5] overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/27c9a636-a959-43d3-87ad-9fb2a842a3d0_compressed_C--Image-202695-04_57_16.webp"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div ref={hero.ref} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 transition-all duration-700 ${hero.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-semibold uppercase">Teaching</span>
          </div>
          <h1 className="font-serif-tc text-[#2c2420] leading-[1.3] mb-6" style={{ fontSize: 'clamp(2rem, 5vw, 2.75rem)' }}>
            教學對象與方式
          </h1>
          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-2xl mb-8">
            學習不是另一種壓力，而是能被生活容納的事。無論你是完全零基礎的成人、想為生活保留安靜的人，或是正在為孩子或自己尋找一個溫柔學習空間——這頁為你說明：什麼樣的人適合這裡、上課方式有哪些，以及學習節奏怎麼安排。
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 text-[0.875rem] text-[#5a4e42] font-medium">
              <i className="ri-user-heart-line text-[#b08060]"></i>
              誰適合這裡
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 text-[0.875rem] text-[#5a4e42] font-medium">
              <i className="ri-stack-line text-[#b08060]"></i>
              上課方式
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 text-[0.875rem] text-[#5a4e42] font-medium">
              <i className="ri-timer-line text-[#b08060]"></i>
              學習節奏
            </span>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={audience.ref} className={`transition-all duration-700 ${audience.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-semibold uppercase">Who Is This For</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4]" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                適合你的人
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* For */}
              <div className="bg-[#f5f0e8] rounded-2xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#4a6741]/15">
                    <i className="ri-checkbox-circle-line text-[#4a6741] text-xl"></i>
                  </div>
                  <h3 className="font-serif-tc text-[#2c2420] font-bold" style={{ fontSize: '1.375rem' }}>這裡適合你，如果你⋯⋯</h3>
                </div>
                <ul className="space-y-5">
                  {forItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-leaf-line text-[#4a6741] text-base"></i>
                      </div>
                      <p className="text-[1.0625rem] leading-[1.7] text-[#4a3e32]">{item}</p>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Not For */}
              <div className="bg-[#f7f3ee] rounded-2xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#9a8470]/15">
                    <i className="ri-information-line text-[#9a8470] text-xl"></i>
                  </div>
                  <h3 className="font-serif-tc text-[#2c2420] font-bold" style={{ fontSize: '1.375rem' }}>也許暫時不適合的人</h3>
                </div>
                <p className="text-[0.9375rem] leading-[1.75] text-[#7a6e62] mb-6">
                  以下情況可能和這裡的步調不太一致。如果想法改變了，隨時歡迎回來看看。
                </p>
                <ul className="space-y-5">
                  {notForItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-subtract-line text-[#b0a090] text-base"></i>
                      </div>
                      <div>
                        <p className="text-[1.0625rem] leading-[1.6] text-[#5a4e42] font-medium">{item.text}</p>
                        <p className="text-[0.8125rem] leading-[1.6] text-[#9a8470] mt-0.5">{item.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods */}
      <section className="py-24 md:py-32 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={methodSec.ref} className={`transition-all duration-700 ${methodSec.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-semibold uppercase">How We Learn</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4]" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                上課方式
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              {methods.map((m, i) => (
                <div key={i} className="bg-[#faf8f5] rounded-2xl p-8 md:p-10 flex flex-col">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#ede8e0] mb-6">
                    <i className={`${m.icon} text-[#7a6248] text-2xl`}></i>
                  </div>
                  <h3 className="font-serif-tc text-[#2c2420] mb-3 font-bold" style={{ fontSize: '1.375rem' }}>{m.title}</h3>
                  <p className="text-[1rem] leading-[1.7] text-[#7a6248] font-medium mb-6">{m.tagline}</p>

                  {/* Details */}
                  <div className="space-y-4 flex-1">
                    <div className="flex gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-user-star-line text-[#b08060] text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[0.75rem] tracking-[0.1em] text-[#9a8470] font-semibold uppercase mb-1">適合對象</p>
                        <p className="text-[0.9375rem] leading-[1.65] text-[#4a3e32]">{m.audience}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-sun-line text-[#b08060] text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[0.75rem] tracking-[0.1em] text-[#9a8470] font-semibold uppercase mb-1">上課氛圍</p>
                        <p className="text-[0.9375rem] leading-[1.65] text-[#4a3e32]">{m.atmosphere}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-timer-flash-line text-[#b08060] text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[0.75rem] tracking-[0.1em] text-[#9a8470] font-semibold uppercase mb-1">學習節奏</p>
                        <p className="text-[0.9375rem] leading-[1.65] text-[#4a3e32]">{m.rhythm}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-lightbulb-flash-line text-[#b08060] text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[0.75rem] tracking-[0.1em] text-[#9a8470] font-semibold uppercase mb-1">適合情境</p>
                        <p className="text-[0.9375rem] leading-[1.65] text-[#4a3e32]">{m.scenario}</p>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#ede5d8]">
                    {m.tags.map((tag, j) => (
                      <span key={j} className="px-3 py-1 rounded-full text-[0.8125rem] font-medium bg-[#ede8e0] text-[#7a6248]">{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Lesson format note */}
            <div className="bg-white rounded-2xl border border-[#ede5d8] p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f5f0e8] shrink-0">
                <i className="ri-building-2-line text-[#7a6248] text-xl"></i>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-serif-tc text-[#2c2420] font-bold" style={{ fontSize: '1.0625rem' }}>實體課</h4>
                  <span className="text-[0.75rem] text-[#7a6248] bg-[#f5f0e8] px-2.5 py-0.5 rounded-full font-medium">每堂 60 分鐘</span>
                </div>
                <p className="text-[0.9375rem] leading-[1.7] text-[#5a4e42]">
                  上課地點在台中豐原教室。無論是一對一或小班制，都以實體面對面方式進行——這樣可以即時調整手型與觸弦方式，也是最能傳遞溫度的學習形式。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LessonFlowSection />

      {/* Rhythm */}
      <section className="py-24 md:py-32 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={rhythm.ref} className={`transition-all duration-700 ${rhythm.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-7">
                  <div className="divider-gold"></div>
                  <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-semibold uppercase">Learning Rhythm</span>
                </div>
                <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-6" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                  學習節奏說明
                </h2>
                <p className="text-[1.0625rem] leading-[1.75] text-[#5a4e42] mb-4">
                  這裡沒有固定的進度壓力。你的生活狀態，才是決定學習節奏最重要的依據。
                </p>
                <p className="text-[1.0625rem] leading-[1.75] text-[#5a4e42] mb-4">
                  學習不是另一種讓自己疲憊的事。當你在課後感到放鬆、充實，才是真正對的節奏。以下是四個關於學習節奏的核心原則：
                </p>
                <div className="hidden lg:block mt-8">
                  <div className="flex items-center gap-3 text-[0.875rem] text-[#9a8470]">
                    <i className="ri-information-line"></i>
                    <span>這些不是規定，是我們一起找到的默契</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {rhythmItems.map((item, i) => (
                  <div
                    key={i}
                    className="bg-[#faf8f5] rounded-xl p-6 flex gap-4 transition-all duration-300 hover:translate-x-1"
                  >
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0 mt-1"
                      style={{ backgroundColor: `${item.color}15` }}
                    >
                      <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
                    </div>
                    <div>
                      <p className="text-[0.75rem] tracking-[0.12em] text-[#9a8470] font-semibold uppercase mb-1.5">{item.q}</p>
                      <p className="text-[1rem] leading-[1.7] text-[#4a3e32]">{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div ref={cta.ref} className={`w-full px-6 md:px-12 lg:px-20 text-center transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="divider-gold"></div>
              <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-semibold uppercase">Next Step</span>
              <div className="divider-gold"></div>
            </div>
            <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-5" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
              感覺這裡的方式適合你嗎？
            </h2>
            <p className="text-[1.0625rem] leading-[1.75] text-[#5a4e42] mb-10">
              不需要立刻決定。先加入 LINE@ 聊聊你現在的狀態——你想學古箏的原因、你有什麼期待、你擔心什麼——我們一起確認這裡是否適合你。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://line.me"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-9 py-4 rounded-full bg-[#4a3e32] text-[#f5f0e8] text-[1rem] font-bold hover:bg-[#2c1f12] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-line-line text-lg"></i>
                加入 LINE@ 諮詢
              </a>
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 px-9 py-4 rounded-full border-2 border-[#c8b89a] text-[#7a6248] text-[1rem] font-semibold hover:bg-[#7a6248] hover:text-white hover:border-[#7a6248] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-right-line"></i>
                查看學習路徑
              </Link>
            </div>
            <p className="text-[0.8125rem] text-[#9a8470] mt-8">不需要任何準備，帶著你的好奇就好</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}